

























































































import Vue from 'vue';
import {
  Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import {
  KCardFooterBtn,
} from '@kasasa/fbase-components';
import { Alert, NoticeClass, AuthStore } from '@kasasa/fbase-components/lib';
import { ATMSettings, ATMSettingsResource } from '@/api/atm';
import { InterestSettings, InterestSettingsResource } from '@/api/interestsettings';
import { RewardSummary, RewardSummaryResource } from '@/api/rewardsummary';
import {
  FiConfiguration, FiConfigurationResource, ReportResource, Report,
} from '@/api/reports';
import { Cycle } from '@/api/cycles';
import ReportDownloader from '@/components/reports/ReportDownloader.vue';

interface RewardSummaryTableEntry {
  rewardSummary: RewardSummary,
  expanded: boolean,
  children: RewardSummaryTableEntry[] | null
}

@Component({
  computed: mapState(['fiId']),
  components: {
    KCardFooterBtn,
    ReportDownloader,
  },
})
export default class RewardsSummaryCard extends Vue {
  @Prop({ default: null }) cycle!: Cycle | null;

  fiId!: string;

  loading: boolean = true;

  headers: DataTableHeader[] = [];

  items: RewardSummaryTableEntry[] = [];

  atmSettings: ATMSettings | null = null;

  interestSettings: InterestSettings | null = null;

  reportExists: boolean = false;

  reportFormat: string | null = null;

  downloadReportFileName: string | null = null;

  @Inject('ATMSettingsResource')
  atmSettingsResource!: ATMSettingsResource;

  @Inject('InterestSettingsResource')
  interestSettingsResource!: InterestSettingsResource;

  @Inject('RewardSummaryResource')
  rewardSummaryResource!: RewardSummaryResource;

  @Inject('FiConfigurationResource')
  fiConfigurationResource!: FiConfigurationResource;

  @Inject('ReportResource')
  reportResource!: ReportResource;

  mounted() {
    this.loadData();
  }

  @Watch('cycle')
  async loadData() {
    if (this.cycle) {
      this.loading = true;
      this.items = [];

      this.checkReportExists();

      await this.loadATMSettings();
      await this.loadInterestSettings();

      this.buildHeaders();

      await this.rewardSummaryResource.getRewardSummary(this.fiId, this.cycle.id, null)
        .then((results: RewardSummary[]) => {
          results.forEach((summary: RewardSummary) => {
            const productEntry: RewardSummaryTableEntry = {
              rewardSummary: summary,
              expanded: false,
              children: null,
            };
            this.items.push(productEntry);
          });
        }).catch((error: any) => {
          this.showErrorMessage(error);
        }).finally(() => {
          this.loading = false;
        });
    }
  }

  async checkReportExists() {
    this.reportExists = false;
    this.reportFormat = null;
    this.fiConfigurationResource.getFiConfiguration(this.fiId)
      .then((config: FiConfiguration) => {
        this.reportFormat = config.fiReportFileFormat;
        if (this.cycle) {
          this.reportResource.getReportInfos(this.fiId, this.cycle.id, config.fiReportFileFormat)
            .then((results: Report[]) => {
              const expectedReportName = `Rewards_Report.${config.fiReportFileFormat.toLowerCase()}`;
              for (let i = 0; i < results.length; i++) {
                if (results[i].filename === expectedReportName) {
                  this.reportExists = true;
                  break;
                }
              }
            });
        }
      }).catch((error: any) => {
        this.showErrorMessage(error);
      });
  }

  async loadATMSettings() {
    this.atmSettings = null;
    await this.atmSettingsResource.getATMSettings(this.fiId)
      .then((results: ATMSettings) => {
        this.atmSettings = results;
      }).catch((error: any) => {
        this.showErrorMessage(error);
      });
  }

  async loadInterestSettings() {
    this.interestSettings = null;
    await this.interestSettingsResource.getInterestSettings(this.fiId)
      .then((results: InterestSettings) => {
        this.interestSettings = results;
      }).catch((error: any) => {
        this.showErrorMessage(error);
      });
  }

  populateChildrenIfNecessary(productEntry: RewardSummaryTableEntry) {
    if (productEntry.children === null && this.cycle) {
      this.rewardSummaryResource.getRewardSummary(this.fiId, this.cycle.id, productEntry.rewardSummary.productId)
        .then((results: RewardSummary[]) => {
          const children: RewardSummaryTableEntry[] = [];
          results.forEach((summary: RewardSummary) => {
            const childEntry: RewardSummaryTableEntry = {
              rewardSummary: summary,
              expanded: false,
              children: null,
            };
            children.push(childEntry);
          });
          /* eslint-disable no-param-reassign */
          productEntry.children = children;
          /* eslint-enable no-param-reassign */
        }).catch((error: any) => {
          this.showErrorMessage(error);
        });
    }
  }

  buildHeaders() {
    this.headers = [];
    this.headers.push({ text: '', value: 'toggle', width: '1px' });
    this.headers.push({ text: 'Product', value: 'productName' });
    this.headers.push({ text: 'Branch', value: 'branch' });

    if (this.atmSettings) {
      if (this.atmSettings.atmRefundAggregatorSetting === 'SEPARATE') {
        this.headers.push({ text: 'ATM Terminal Fees', value: 'totalAtmTerminalFeeAmount' });
        this.headers.push({ text: 'Foreign ATM Usage Fees', value: 'totalForeignAtmUsageFeeAmount' });
      } else if (this.atmSettings.atmRefundAggregatorSetting === 'COMBINE_ALL') {
        this.headers.push({ text: 'ATM Rewards', value: 'totalAtmFeeRefundAmount' });
      }
    }
    this.headers.push({ text: 'Cash Back Amount', value: 'totalCashBackAmount' });
    this.headers.push({ text: 'Net Service Fee Amount', value: 'totalNetServiceFeeAmount' });

    if (this.interestSettings) {
      if (this.interestSettings.coreInterestRateCalculation === 'QUAL_RATE') {
        this.headers.push({ text: 'Interest Adjustment', value: 'totalInterestAdjustment' });
      } else {
        this.headers.push({ text: 'Kasasa Interest Calculation', value: 'kasasaInterestCalculation' });
      }
    }

    this.headers.push({ text: 'Tunes Credit Paid', value: 'totalTunesCreditPaid' });

    this.headers.push({ text: 'Eats Rewards', value: 'totalEatsRewardsPaid' });
    this.headers.push({ text: 'Streaming Rewards', value: 'totalStreamingRewardsPaid' });
  }

  getTableValue = (item: RewardSummary, header: DataTableHeader): String => {
    switch (header.value) {
      case 'productName':
        return item.rewardSummary.productName;
      case 'branch':
        return item.rewardSummary.branch;
      case 'kasasaInterestCalculation':
        return RewardsSummaryCard.formatDollarAmount(Number(item.rewardSummary.totalQualifiedInterestAccrued) + Number(item.rewardSummary.totalNonQualifiedInterestAccrued));
      default:
        return RewardsSummaryCard.formatDollarAmount(item.rewardSummary[header.value]);
    }
  }

  downloadReport() {
    if (this.reportFormat) {
      this.downloadReportFileName = `Rewards_Report.${this.reportFormat.toLowerCase()}`;
    }
  }

  static formatDollarAmount(value: any) {
    return `${value !== null ? `$${Number(value).toFixed(2)}` : ''}`;
  }

  showErrorMessage(error: string) {
    const al = new Alert(error, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
